import React from "react";
import { connect } from "react-redux";
import { ThunkDispatch } from "redux-thunk";
import {
  IAppState,
  ErrorDisplay,
  LoadingDisplay,
  LoadingState,
  ResponseError,
  ApiRequest
} from "../../../../Common";
import { Topic } from "../../../Models/Topic";
import TopicSummaryGrid from "./TopicSummaryGrid";
import { TopicQuickMetrics } from "../../../Models/TopicQuickMetrics";
import { endpoints } from "../../../Constants/endpoints";
import ResponseHandler from "../../../../App/ResponseHandler";
import { ResponseResult } from "../../../../Common/Models/ResponseResult";

type Props = {};

type StateProps = {
  topics: Topic[];
  topicsLoadingState: LoadingState;
  topicsLoadingError: ResponseError;
};

type State = {
  topicMetricsLoadingState: LoadingState;
  topicMetricsLoadingError: ResponseError;
  loadedMetrics: Record<string, TopicQuickMetrics>;
};

function mapStateToProps(state: IAppState): StateProps {
  return {
    topics: state.app.topics,
    topicsLoadingState: state.app.topicsLoadingState,
    topicsLoadingError: state.app.topicsLoadingError
  };
}

class TopicSummaryGridContainer extends React.Component<Props & StateProps, State> {
  constructor(props: Props & StateProps) {
    super(props);

    this.state = {
      topicMetricsLoadingState: {
        isLoaded: false,
        isLoading: false,
        errored: false
      },
      topicMetricsLoadingError: {
        errorDescription: "",
        errorResult: ""
      },
      loadedMetrics: {}
    };
  }

  componentDidMount() {
    this.loadIfNeeded();
  }

  componentDidUpdate() {
    this.loadIfNeeded();
  }

  setLoaded(loadedMetrics: Record<string, TopicQuickMetrics>) {
    this.setState((prevState: State) => ({
      topicMetricsLoadingState: {
        errored: false,
        isLoading: false,
        isLoaded: true
      },
      loadedMetrics
    }));
  }

  setErrored(statusCode: number, errorDesc: string) {
    this.setState((prevState: State) => ({
      topicMetricsLoadingState: {
        errored: true,
        isLoading: false,
        isLoaded: false
      },
      topicMetricsLoadingError: {
        errorCode: statusCode,
        errorDescription: errorDesc,
        errorResult: "Could not display the topic metrics"
      }
    }));
  }

  loadIfNeeded() {
    const { isLoaded, isLoading, errored } = this.state.topicMetricsLoadingState;
    if (this.props.topicsLoadingState.isLoaded && !isLoaded && !isLoading && !errored) {
      this.load();
    }
  }

  load() {
    // set state to loading
    this.setState(
      (prevState: State) => ({
        topicMetricsLoadingState: {
          isLoaded: false,
          isLoading: true,
          errored: false
        },
        loadedMetrics: {}
      }),
      () => {
        // make the API request to get the metrics for this app
        ApiRequest.fromPath(endpoints.topics.allMetrics())
          .withAuthHeader()
          .withJson({
            Codes: this.props.topics.map(t => t.code)
          })
          .post()
          .then((response: Response) => {
            ResponseHandler.handle(
              response,
              (result: ResponseResult) => {
                this.setLoaded(result.payload);
              },
              (result: ResponseResult) => {
                this.setErrored(response.status, result.error.message);
              },
              () => {
                this.setErrored(response.status, "An unknown error has occured");
              }
            );
          });
      }
    );
  }

  render() {
    if (this.props.topicsLoadingState.errored) {
      return (
        <ErrorDisplay
          errorCode={this.props.topicsLoadingError.errorCode}
          errorDescription={this.props.topicsLoadingError.errorDescription}
          errorResult="Could not display the topic summary grid"
        />
      );
    }
    if (this.state.topicMetricsLoadingState.errored) {
      return (
        <ErrorDisplay
          errorCode={this.state.topicMetricsLoadingError.errorCode}
          errorDescription={this.state.topicMetricsLoadingError.errorDescription}
          errorResult="Could not display the topic summary grid- could not load metrics"
        />
      );
    }
    if (this.props.topicsLoadingState.isLoading) {
      return <LoadingDisplay />;
    }
    if (this.props.topicsLoadingState.isLoaded) {
      return (
        <TopicSummaryGrid
          topics={this.props.topics}
          topicMetrics={this.state.loadedMetrics}
          topicMetricsLoadingState={this.state.topicMetricsLoadingState}
        />
      );
    }
    return <></>;
  }
}

export default connect(mapStateToProps)(TopicSummaryGridContainer);
